import React, { useState } from "react";
import security from '../assets/icons/security.svg'
import back from '../assets/icons/arrow_back.svg'
import arrow from '../assets/icons/arrow_right.svg'
import tape from '../assets/icons/yellow_rectangle.svg'
import mail from '../assets/icons/mail.svg'
import psw from '../assets/icons/password.svg'
import phone_ic from '../assets/icons/phone.svg'
import user_ic from '../assets/icons/user.svg'
import '../assets/css/layout/auth.css'
import FormInput from "../components/elements/form_input";
import PhoneInput from "../components/elements/phone_input";
import Button from "../components/elements/button";
import { authApi } from "../network/apis/auth";
import { useNavigate, Link } from 'react-router-dom';
import { showError, showSuccess } from '../components/elements/customError';
import { ToastContainer } from "react-toastify";

const Register = () => {
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: ''
    });
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '' });
    const [showFarmerForm, setShowFarmerForm] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        
        // Check password strength when password changes
        if (name === 'password') {
            checkPasswordStrength(value);
        }
    };

    const checkPasswordStrength = (password) => {
        let score = 0;
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;

        const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
        setPasswordStrength({ score, label: labels[score] || '' });
    };

    const validateForm = () => {
        const { first_name, last_name, email, phone, password, confirmPassword } = formData;

        if (!first_name || !last_name || !email || !phone || !password) {
            showError("All fields are required");
            return false;
        }

        // Name validation
        if (first_name.length < 2 || last_name.length < 2) {
            showError("Names must be at least 2 characters long");
            return false;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showError("Please enter a valid email address");
            return false;
        }

        // Phone validation (must have country code + number)
        const phoneRegex = /^\+\d{1,4}\d{7,12}$/;
        if (!phoneRegex.test(phone)) {
            showError("Please enter a valid phone number");
            return false;
        }

        // Password validation
        if (password.length < 8) {
            showError("Password must be at least 8 characters long");
            return false;
        }

        if (passwordStrength.score < 2) {
            showError("Please choose a stronger password");
            return false;
        }

        // Confirm password
        if (password !== confirmPassword) {
            showError("Passwords do not match");
            return false;
        }

        // Terms agreement
        if (!agreedToTerms) {
            showError("Please agree to the Terms of Service and Privacy Policy");
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        // Prevent default form submission
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        if (!validateForm()) return;
        
        // Prevent double submission
        if (isLoading) return;

        setIsLoading(true);
        
        try {
            const response = await authApi.register({
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email,
                username: formData.email,
                phone: formData.phone,
                password: formData.password
            });
            
            console.log(response.data);
            showSuccess("Registration successful! Please verify your email.");
            
            // Navigate after a short delay to show the success message
            setTimeout(() => {
                navigate('/verify-email', { state: { email: formData.email }, replace: true });
            }, 1000);
        } catch (error) {
            console.error(error);
            
            // Use improved error message from interceptor first
            let errorMessage = error.displayMessage || "Registration failed. Please try again.";
            
            // Handle structured error responses with field-specific errors
            const errorData = error.response?.data;
            
            if (errorData?.errors && typeof errorData.errors === 'object') {
                // Extract the first error from each field and show the first one
                const fieldErrors = Object.entries(errorData.errors);
                if (fieldErrors.length > 0) {
                    const [fieldName, messages] = fieldErrors[0];
                    const message = Array.isArray(messages) ? messages[0] : messages;
                    errorMessage = `${fieldName.replace(/_/g, ' ')}: ${message}`;
                }
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (errorData?.detail) {
                errorMessage = errorData.detail;
            }
            
            showError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <React.Fragment>
            <ToastContainer />
            <div className="auth-container">
                <section className="auth-right">
                    <div className="right-inner">
                        <h3 className="right-inner-title">A platform tailored for the Modern Fish Farmer</h3>
                        <p className="right-inner-text">
                            Join thousands of aquaculture professionals using E-AquaHealth to access 
                            veterinary care, laboratory services, quality supplies, and expert knowledge 
                            to grow their fish farming business.
                        </p>
                        <button className="learn-more-btn" onClick={() => navigate('/about')}>
                            Learn More &nbsp; <img src={arrow} alt="arrow" className="arrow_up"/>
                        </button>
                        <img src={tape} className="tape" alt="decoration" />
                    </div>
                </section>
                <section className="auth-section">
                    <div className="auth-logo-section">
                        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                            E-AQUAHEALTH
                        </Link>
                        <img src={security} alt="security"/>
                    </div>
                    <Link to="/" className="back-home-link">
                        <img src={back} alt="back"/> &nbsp; Back to Home
                    </Link>
                    <div className="spacer"></div>
                    <h3 className="auth-title">Create Account</h3>
                    <p className="auth-subtitle">Join E-AquaHealth and transform your aquaculture business</p>
                    
                    <div className="user-type-section">
                        <p className="user-type-text">Are you a professional in the aquaculture industry?</p>
                        <div className="professional-links register-professional">
                            <Link to="/vet/register" className="professional-link">
                                🩺 Vet
                            </Link>
                            <Link to="/aht/register" className="professional-link">
                                🐟 Animal Health Technologist
                            </Link>
                            <Link to="/lab/register" className="professional-link">
                                🔬 Lab
                            </Link>
                            <Link to="/vendor/register" className="professional-link">
                                🏪 Vendor
                            </Link>
                            <Link to="/logistics/register" className="professional-link">
                                🚚 Logistics
                            </Link>
                        </div>
                        <div className="auth-divider">
                            <span><strong onClick={() => setShowFarmerForm(true)} style={{cursor:'pointer'}} className="professional-link">or register as Fish Farmer</strong></span>
                        </div>
                    </div>

                    {showFarmerForm && (
                        <form className="auth-form" id="farmer-form" onSubmit={handleSubmit} action="#">
                        <div className="form-row">
                            <FormInput
                                id="first_name"
                                label="First Name"
                                required={false}
                                placeholder="First Name"
                                name="first_name"
                                type="text"
                                value={formData.first_name}
                                icon={<img src={user_ic} alt="first name"/>}
                                onChange={handleInputChange}
                                disabled={isLoading}
                            />
                            <FormInput
                                id="last_name"
                                label="Last Name"
                                required={false}
                                placeholder="Last Name"
                                name="last_name"
                                type="text"
                                value={formData.last_name}
                                icon={<img src={user_ic} alt="last name"/>}
                                onChange={handleInputChange}
                                disabled={isLoading}
                            />
                        </div>
                        
                        <FormInput 
                            id="email"
                            label="Email"
                            required={true}
                            icon={<img src={mail} alt="user-email"/>}
                            placeholder="Enter your email"
                            value={formData.email}
                            onChange={handleInputChange}
                            name="email"
                            type="email"
                            disabled={isLoading}
                        />
                        
                        <PhoneInput 
                            label="Phone Number"
                            required={true}
                            showLabel={false}
                            icon={<img src={phone_ic} alt="user-phone"/>}
                            value={formData.phone}
                            onChange={handleInputChange}
                            name="phone"
                            disabled={isLoading}
                            defaultCountryCode="+234"
                        />
                        
                        <FormInput
                            id="password"
                            label="Password"
                            required={true}
                            icon={<img src={psw} alt="password"/>}
                            placeholder="Create a password"
                            value={formData.password}
                            onChange={handleInputChange}
                            name="password"
                            type="password"
                            disabled={isLoading}
                        />
                        
                        {formData.password && (
                            <div className="password-strength">
                                <div className="strength-bars">
                                    {[1, 2, 3, 4, 5].map((level) => (
                                        <div 
                                            key={level}
                                            className={`strength-bar ${passwordStrength.score >= level ? `level-${passwordStrength.score}` : ''}`}
                                        />
                                    ))}
                                </div>
                                <span className={`strength-label level-${passwordStrength.score}`}>
                                    {passwordStrength.label}
                                </span>
                            </div>
                        )}
                        
                        <FormInput
                            id="confirmPassword"
                            label="Confirm Password"
                            required={true}
                            icon={<img src={psw} alt="confirm password"/>}
                            placeholder="Confirm your password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            name="confirmPassword"
                            type="password"
                            disabled={isLoading}
                        />
                            
                        <div className="agreement-section">
                            <input 
                                type="checkbox" 
                                id="agree" 
                                className="agreement-checkbox" 
                                checked={agreedToTerms}
                                onChange={(e) => setAgreedToTerms(e.target.checked)}
                                disabled={isLoading}
                            />
                            <label htmlFor="agree" className="agreement-text">
                                I agree to E-AquaHealth&apos;s{' '}
                                <Link to="/terms" className="terms-link">Terms of Service</Link>
                                {' '}and{' '}
                                <Link to="/privacy" className="privacy-link">Privacy Policy</Link>
                            </label>
                        </div>
                        
                        <Button 
                            text={isLoading ? "Creating Account..." : "Get Started"} 
                            type="submit" 
                            fullWidth 
                            icon={!isLoading && <img src={arrow} alt="submit" className="arrow_up" />}
                            loading={isLoading}
                            disabled={isLoading}
                        />
                        
                        <p className="auth-text">
                            Already have an Account?{' '}
                            <Link to="/login" className="login-link">Log In</Link>
                        </p>
                    </form>
                    )}

                    <ul className="footer-links">
                        <li className="footer-link-item">
                            <Link to="/contact">Contact Us</Link>
                        </li>
                        <li className="footer-link-item">
                            <Link to="/terms">Terms of Service</Link>
                        </li>
                        <li className="footer-link-item">
                            <Link to="/faq">FAQs</Link>
                        </li>
                    </ul>
                </section>
            </div>
        </React.Fragment>
    );
}

export default Register;